import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { Link } from '../../atoms/link/Link';
import PlaceholderImage from '../../atoms/placeholderImage';
import CustomVideo from '../customVideoControls';
import styles from './ProductTile.module.scss';

import { useMedia } from 'react-use';
import { Navigation, Scrollbar } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { ImageWithOverlay } from '../../atoms/image/ImageWithOverlay';

const ProductTileGallery = ({
  images,
  url,
  remote,
  hideArrows,
  productName,
  enableTouchOnMobile = true,
  showSwiper,
  setProductHeight,
  nostoCallback,
  enableGallery = true,
  productId,
  productOverlayUrl,
  preloadImage = false,
  sourceType = '',
}) => {
  const swiperRef = useRef(null);
  const imageRef = useRef();
  const isMobileOrTablet = useMedia('(max-width: 1199px)', false);
  const isFromAutocomplete = sourceType === 'autocomplete';
  const [isTileHovered, setIsTileHovered] = useState(false);

  useEffect(() => {
    setProductHeight && setProductHeight(imageRef?.current?.offsetHeight);
  }, [imageRef?.current?.offsetHeight]);

  // Memo id for individual nav arrow
  // had to use memo to stop id regeneration on event update
  const uniqueClassSuffix = productId;
  // Carousel settings
  const carouselSettings = {
    spaceBetween: 0,
    slidesPerView: 1,
    slidesPerGroup: 1,
    initialSlide: 0,
    observer: true,
    watchOverflow: false,
    loop: true,
    lazy: true,
    preloadImages: true,
    simulateTouch: enableTouchOnMobile,
    allowTouchMove: enableTouchOnMobile,
    scrollbar: {
      draggable: true,
      hide: false,
    },
    navigation: {
      nextEl: `.swiper-next-${uniqueClassSuffix}`,
      prevEl: `.swiper-prev-${uniqueClassSuffix}`,
    },
    modules: [Navigation, Scrollbar],
    breakpoints: {
      1200: {
        scrollbar: false,
        simulateTouch: false,
        allowTouchMove: false,
      },
    },
  };

  // Render image or placeholder
  const renderImage = useCallback((image) => {
    return !image?.url && !image?.videoUrl ? (
      <PlaceholderImage productName="no image available" />
    ) : (
      <>
        {image.videoUrl ? (
          <CustomVideo hideControls={true} videoUrl={image.videoUrl} alt={productName} />
        ) : (
          <ImageWithOverlay
            alt={productName}
            image={image.url || image.url.url}
            remote={remote}
            overlayUrl={productOverlayUrl}
            preloadImage={preloadImage}
            opts={{
              sirvWidth: 800,
              sirvHeight: 1000,
            }}
          />
        )}
      </>
    );
  }, []);

  // Render swiper
  const renderSwiper = useMemo(() => {
    return (
      enableGallery &&
      images &&
      images.length > 1 && (
        <Swiper {...carouselSettings} ref={swiperRef} className="gallery-swiper" nested>
          {images?.slice(0, Math.min(images.length, 5)).map((item, index) => {
            return (
              <SwiperSlide key={`${item.id}_${index}`} index={index}>
                {item.videoUrl ? (
                  <Link
                    link={url}
                    className={styles.imageWrapper}
                    onClick={nostoCallback}
                    ariaLabel={`video_${productName}`}
                  >
                    <CustomVideo
                      hideControls={true}
                      videoUrl={item.videoUrl}
                      overlayUrl={productOverlayUrl}
                      alt={productName}
                    />
                  </Link>
                ) : (
                  <Link
                    link={url}
                    className={styles.imageWrapper}
                    onClick={nostoCallback}
                    ariaLabel={`image_${productName}`}
                  >
                    {renderImage(item)}
                  </Link>
                )}
              </SwiperSlide>
            );
          })}
        </Swiper>
      )
    );
  }, [images, showSwiper]);

  const renderImages = () => {
    if (enableGallery && images && images.length >= 2) {
      const hasVideo = images[0]?.videoUrl || images[1]?.videoUrl;

      if (hasVideo) {
        return (
          <Link link={url} className={styles.imageWrapper} onClick={nostoCallback} ariaLabel={`image_${productName}`}>
            <div className={styles.imageContainer}>
              <div className={clsx(styles.imageBox, { [styles.imageBoxHidden]: isTileHovered })}>
                {renderImage(images[0])}
              </div>
              <div
                className={clsx(styles.imageBox, styles.imageBoxOverlay, { [styles.imageBoxVisible]: isTileHovered })}
              >
                {renderImage(images[1])}
              </div>
            </div>
          </Link>
        );
      } else {
        return (
          <Link link={url} className={styles.imageWrapper} onClick={nostoCallback} ariaLabel={`image_${productName}`}>
            {renderImage(isTileHovered ? images[1] : images[0])}
          </Link>
        );
      }
    }
    if (images.length === 1) {
      return (
        <Link link={url} className={styles.imageWrapper} onClick={nostoCallback} ariaLabel={`image_${productName}`}>
          {renderImage(images[0])}
        </Link>
      );
    }

    return null;
  };

  // Return placeholder if no images provided
  if (!images || images.length === 0) {
    return (
      <div
        className={clsx(styles.tileGallery, {
          [styles.hideArrows]: hideArrows,
          [styles.isFromAutocomplete]: isFromAutocomplete,
        })}
        ref={imageRef}
      >
        <div className={styles.tileGalleryInner}>
          {url && url !== undefined ? (
            <Link link={url} className={styles.imageWrapper} onClick={nostoCallback} ariaLabel={`image_${productName}`}>
              <PlaceholderImage productName="no image available" />
            </Link>
          ) : (
            <PlaceholderImage productName="no image available" />
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      className={clsx(styles.tileGallery, {
        [styles.hideArrows]: hideArrows,
        [styles.isFromAutocomplete]: isFromAutocomplete,
      })}
      ref={imageRef}
    >
      <div
        className={styles.tileGalleryInner}
        onMouseOver={() => {
          setIsTileHovered(true);
        }}
        onMouseLeave={() => {
          setIsTileHovered(false);
        }}
      >
        {isMobileOrTablet && images.length > 1 ? renderSwiper : renderImages()}
      </div>
    </div>
  );
};

ProductTileGallery.displayName = 'ProductTileGallery';

export default ProductTileGallery;
